import { useEffect, useState } from "react";

type DevicePlatform = "android" | "ios" | "macos" | "windows" | "desktop" | "unknown";
type DeviceType = "mobile" | "tablet" | "desktop" | "unknown";
type Browser = "safari" | "chrome" | "firefox" | "edge" | "unknown";

export const useDeviceDetect = () => {
    const [device, setDevice] = useState<{
        platform: DevicePlatform;
        type: DeviceType;
        browser: Browser;
        osVersion: string | null;
        isTouchDevice: boolean;
    }>({
        platform: "unknown",
        type: "unknown",
        browser: "unknown",
        osVersion: null,
        isTouchDevice: false,
    });

    useEffect(() => {
        const ua = navigator.userAgent.toLowerCase();
        const maxTouchPoints = navigator.maxTouchPoints || 0;
        const isTouchDevice = maxTouchPoints > 1;

        const isAndroid = /android/.test(ua);
        const isIphone = /iphone/.test(ua);
        const isIpod = /ipod/.test(ua);
        const isIpad = /ipad/.test(ua) || (/(macintosh|mac os x)/.test(ua) && isTouchDevice);
        const isIOS = isIphone || isIpod || isIpad;
        const isWindows = /win/.test(ua);
        const isMacOS = /macintosh|mac os x/.test(ua) && !isIOS;

        const isSafari = /safari/.test(ua) && !/chrome|android/.test(ua);
        const isChrome = /chrome/.test(ua) && !/edg/.test(ua);
        const isFirefox = /firefox/.test(ua);
        const isEdge = /edg/.test(ua);

        let platform: DevicePlatform = "unknown";
        if (isAndroid) platform = "android";
        else if (isIOS) platform = "ios";
        else if (isWindows) platform = "windows";
        else if (isMacOS) platform = "macos";
        else if (!isTouchDevice) platform = "desktop";

        let type: DeviceType = "desktop";
        if (isAndroid) type = /mobile/.test(ua) ? "mobile" : "tablet";
        else if (isIphone || isIpod) type = "mobile";
        else if (isIpad) type = "tablet";
        else if (isTouchDevice && platform === "macos") type = "tablet";

        let browser: Browser = "unknown";
        if (isSafari) browser = "safari";
        else if (isChrome) browser = "chrome";
        else if (isFirefox) browser = "firefox";
        else if (isEdge) browser = "edge";

        // OS version detection
        let osVersion: string | null = null;

        if (isAndroid) {
            const match = ua.match(/android\s([0-9._]+)/);
            osVersion = match ? match[1].replace(/_/g, ".") : null;
        } else if (isIOS) {
            const match = ua.match(/os\s([0-9_]+)\slike mac os x/);
            osVersion = match ? match[1].replace(/_/g, ".") : null;
        } else if (isMacOS) {
            const match = ua.match(/mac os x\s([0-9_]+)/);
            osVersion = match ? match[1].replace(/_/g, ".") : null;
        } else if (isWindows) {
            const match = ua.match(/windows nt\s([0-9.]+)/);
            osVersion = match ? match[1] : null;
        }

        setDevice({
            platform,
            type,
            browser,
            osVersion,
            isTouchDevice,
        });
    }, []);

    return device;
};
